import { Schema } from './schema.js';
import { Property, Type, TypeKind, TypeReference, UnionType } from './types.js';
import { ExpressionConstraint, JsExpressionConstraint, OclExpressionConstraint } from './constraint.js';
import { ModelAvailability } from './internal/ModelAvailability.js';
import { pathToRoot, PropertyPathWalker, PropertyValue } from './internal/PropertyPathWalker.js';
import { PropertyValueIterable } from './internal/PropertyValueIterable.js';
import { DeepModelWalker } from './internal/ModelWalker.js';
import { ModelSystemProperties } from './internal/ModelSystemProperties.js';
import deepEqual from 'deep-equal';
import { Option, OptionGroup, SourcedValueOption, SourcedValueOptions } from './option.js';
import { SinglePropertyMutator } from './internal/SinglePropertyMutator.js';
import { OclEngine, OclParser } from '@canvas-logic/ocl';
import { deepCopy } from './deep-copy.js';

import { ModelChecker } from './internal/ModelChecker.js';
import {
  Engine,
  IEntitySearchResult,
  IOptionMutatorFactory,
  LookupId,
  Model,
  ModelMutator,
  ModelOptionMutator,
  MutatorContext,
  PModel,
  POption,
  PropertyPath,
  PropertyValues,
  ValidationResult
} from './engine.js';
import { Product } from './product.js';
import { ComponentSearchVisitor, PrototypeTraverser, SimpleVisitor } from './internal/PrototypeVisitor.js';
import { engineUtils, FlatStructuralTransformer, getTypeRef } from './index.js';
import { EngineNotificationsService } from './services/notifications/EngineNotificationsService.js';
import { IdentityStructuralTransformer } from './services/transformers/IdentityStructuralTransformer.js';
import { IStructuralTransformer, TransformerResultType } from './services/transformers/StructuralTransformer.js';
import { FilterRegistry } from './services/FilterRegistry.js';
import { CompatibilityService } from './compatibility/CompatibilityService.js';
import { CompatibilityTable } from './compatibility/CompatibilityTable.js';
import { isRestrictedTo } from './compatibility/CompatibleValues.js';
import { ProductWithContext } from './dataset.js';
import { ResolutionSchema } from './lookup/ResolutionSchema.js';
import { TypeService } from './services/TypeService.js';
import { CompatibilityFilter } from './compatibility/CompatibilityFilter.js';
import { CSPSolver } from './compatibility/CSPSolver.js';
import { CSPUtils } from './compatibility/CSPUtils.js';
import {
  extendedSinglePropertyLookupTypeGuard,
  LookupRecord,
  multiPropertiesLookupRecordTypeGuard,
  singlePropertyLookupTypeGuard
} from './lookup/ResolutionSchemaDefinition.js';
import { AggregateFunction, keepFirstAggregateFunction, ObjectHashSetWithPayload } from './lookup/ObjectHashSetWithPayload.js';

export const typeSymbol = Symbol.for('__type__');
export const optionIdSymbol = Symbol.for('__optionId__');
export const sourcesSymbol = Symbol.for('sources');
export const unAvailabilitySymbol = Symbol.for('unavailable');


export class EngineInternalAPI {
  static relinkToModel(schema: Schema, model: Model, path: PropertyPath): Model {
    let propertyValue = new PropertyPathWalker(path, schema, model, getTypeRef(model)).getPropertyValue();
    let resultingModel = EngineInternalAPI.prepareModel(schema, propertyValue.property.type, propertyValue.value);
    propertyValue.parent.value[propertyValue.property.name] = resultingModel;
    return resultingModel;
  }

  /***
   * Builds model with symlinks based on type and prototype
   * @param schema - schema
   * @param typeRef - type of the prototype
   * @param prototype - plain model-object
   */
  static prepareModel(schema: Schema, typeRef: TypeReference, prototype: {}): PModel {
    const functor = new PrototypeTraverser(schema, typeRef, prototype);
    const visitor = new SimpleVisitor(schema);
    const model = functor.map(visitor);
    return model;
  };

  static flatOptions(source: [Option | OptionGroup]): Option[] {
    function flatMap(o: Option | OptionGroup): Option[] {
      if (o instanceof Option) {
        return [o];
      } else {
        return (o as OptionGroup).children.reduce((acc, x) => acc.concat(flatMap(x)), []);
      }
    }

    return (source as any).reduce((acc, x) => acc.concat(flatMap(x)), []);
  }

  static mapOptions(source: SourcedValueOptions, f: (option: Option | null) => Option): SourcedValueOptions {
    function process(o: SourcedValueOption): SourcedValueOption {
      if (o instanceof Option) {
        return f(o);
      } else {
        let newOG = deepCopy((o as OptionGroup), ['children']);
        newOG.children = (o as OptionGroup).children.map(process).filter(child => child !== null);
        return newOG;
      }
    }

    return source.map(process);
  }
}

export class EngineImpl implements Engine, MutatorContext {

  public static skipModelCopyOnMutate = false;
  public schema!: Schema;
  private typeRef!: TypeReference;
  private constraints: ExpressionConstraint[] = [];
  private availabiltyService: ModelAvailability = new ModelAvailability();
  public compatibilityService: CompatibilityService;
  private context: EngineContext = new EngineContext();
  readonly notifications = new EngineNotificationsService();
  private filterRegistry: FilterRegistry;
  private optimizationFlags = {
    useOneCopyOfModel: true,
    lookupValuesEarlyExist: false,
    skipModelCheck: true,
    skipModelCopyOnMutate: true,
    skipOptionCopyOnMutate: true,
    /*
      TODO: Implement
      1. inject plugin in model, and all options separately.
      2. add flag make extendWithPrototype method skippable
     */
    optimizePluginInjection: true,
  }


  addOclConstraint(oclExpression: string, errorMessage: string): void {
    this.constraints.push(new OclExpressionConstraint(oclExpression, errorMessage, this.schema));
  }

  addJsConstraint(model: Model, constraint: JsExpressionConstraint): ValidationResult {
    this.constraints.push(constraint);
    let validationResult = this.validate(model);
    if (validationResult.isInvalid) {
      this.constraints.pop();
    }
    return validationResult;
  }

  propertyValuesByPath(model: PModel, propertyPath: string): SourcedValueOptions | boolean[] | number[] | string[] | undefined;
  propertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | boolean[] | number[] | string[] | undefined;
  propertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>, filterNames: string[]): TransformerResultType<IStructuralTransformer<T>> | number[] | string[] | undefined;
  propertyValuesByPath(model: PModel, propertyPath: string, transformer: IdentityStructuralTransformer = new IdentityStructuralTransformer(), filterNames: string[] = []): SourcedValueOptions | boolean[] | number[] | string[] | undefined {
    let type = getTypeRef(model) ?? this.typeRef;
    let propertyValue: PropertyValue | undefined = new PropertyPathWalker(propertyPath, this.schema, model, type).getPropertyValue();
    if (!propertyValue) {
      return undefined;
    }
    let values = new PropertyValueIterable(this.schema, model, propertyPath).values;
    // Enum type is primitive so doesn't need a preparing
    if (values && isSourcedValues(values)) {
      let resultValues = values;
      if (filterNames.length) {
        resultValues = this.filterOptionsWithFilters(model, propertyValue, propertyPath, values, filterNames);
      }
      return this.optionValues(transformer, resultValues);
    } else {
      if (filterNames.length > 0) {
        throw new Error('Filtering of enum values are not supported');
      }
      return values;
    }
  }

  private filterOptionsWithFilters(model: PModel, propertyValue: PropertyValue, propertyPath: PropertyPath, options: SourcedValueOptions, filterNames: string[]): SourcedValueOptions {
    const filters = this.filterRegistry?.getFiltersByName(filterNames) ?? [];
    const propertyType = this.schema.getType(propertyValue.property.type);
    const propertyTypeRef = propertyType.getTypeReference();

    const result = this.filterOptionGroup(options, propertyTypeRef, (propValue: Option) => {
      for (let filter of filters) {
        if (filter.definition.type !== propValue.type.typeRef) {
          continue;
        }
        let isValid = filter.expression.evaluate(propValue.model, model);
        if (!isValid) {
          return false;
        }
      }
      return true;
    });
    return result;
  }

  optionValuesByPath(model: PModel, propertyPath: string): SourcedValueOptions | undefined;
  optionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;
  optionValuesByPath(model: PModel, propertyPath: string, transformer: IdentityStructuralTransformer = new IdentityStructuralTransformer()): SourcedValueOptions | undefined {
    let type = (model as any)[typeSymbol] ? (model as any)[typeSymbol] : this.typeRef;
    let propertyValue: PropertyValue | undefined = new PropertyPathWalker(propertyPath, this.schema, model, type).getPropertyValue();
    if (!propertyValue) {
      return undefined;
    }
    let values = new PropertyValueIterable(this.schema, model, propertyPath).values;
    if (isSourcedValues(values)) {
      return this.optionValues(transformer, values);
    }
    return undefined;
  }

  private optionValues(transformer: IdentityStructuralTransformer, values: Array<Option | OptionGroup>) {
    const optionsResult = EngineInternalAPI.mapOptions(transformer.transform(values), (o) => {
      let newOption = deepCopy(o, ['model']);
      /**
       * Model preparation is called on already prepared model here, which may be unnecessary.
       * From quick look there are some symbols (namely, type symbol) which is not cloned
       * via deep-copy, thus we enrich newly created object with it in 'prepareModel'.
       *
       * @todo: consider deeper research and possibility to remove this call and replace it with
       *        explicit symbol transfer
       */
      newOption.model = EngineInternalAPI.prepareModel(this.schema, o.type.typeRef, o.model);
      //TODO: Investigate the reason of this line
      //SourceUtils.setOptionId(newOption.model, newOption._id);
      return newOption;
    });
    return optionsResult;
  }

  setSourcedOptionToCompatible(model: PModel) {
    let modelWalker: DeepModelWalker = new DeepModelWalker(this.schema, model, this.typeRef);
    let propertyValue: PropertyValue | undefined;
    while (propertyValue = modelWalker.next()) {
      if (ModelSystemProperties.isAvailable(propertyValue) &&
        propertyValue.property.opts.needSource &&
        ModelSystemProperties.getSource(propertyValue) &&
        //just skip for now
        !Array.isArray(ModelSystemProperties.getSource(propertyValue)) &&
        ModelSystemProperties.getSource(propertyValue)!.getOptionAvailability()) {
        let currentValueNotPassAvailabilityCheck = false;
        let currentOption;
        for (let source of ModelSystemProperties.getSource(propertyValue)!.getSource()) {
          // if (source === AllOptionsFromType || source instanceof POptionGroup){
          //
          // }
          if (deepEqual((source as POption).model, propertyValue.value)) {
            currentOption = source;
          }
        }
        for (let constraint of ModelSystemProperties.getSource(propertyValue)!.getOptionAvailability()!) {
          if (!constraint.evaluate(model, currentOption)) {
            currentValueNotPassAvailabilityCheck = true;
            break;
          }
        }
        if (currentValueNotPassAvailabilityCheck) {
          const path = pathToRoot(propertyValue);
          let propertyValueIterable = new PropertyValueIterable(this.schema, model, path);
          let propVal;
          let foundCompatible = false;
          while (!foundCompatible && (propVal = propertyValueIterable.next())) {
            let skipOption = false;
            for (let constraint of ModelSystemProperties.getSource(propertyValue)!.getOptionAvailability()!) {
              if (!constraint.evaluate(model, propVal)) {
                skipOption = true;
                break;
              }
            }

            if (!skipOption && this.powerfulMutate(model, new SinglePropertyMutator(propertyValue.property.name, propVal))[1].isValid) {
              propertyValue.parent!.value[propertyValue.property.name] = (propVal as POption).model;
              foundCompatible = true;
            }
          }
          if (!foundCompatible) {
            ModelSystemProperties.addToUnAvailability(propertyValue);
            propertyValue.parent!.value[propertyValue.property.name] = undefined;
          }
        }
      }
    }
  }

  compatibleOptionsByMutator(model: PModel, factory: IOptionMutatorFactory, options: POption[]): POption[] {
    const result = [];
    for (let option of options) {
      const mutator = factory.create(option);

      const [updatedModel, validationResult] = this.mutate(model, mutator);
      if (validationResult.isValid) {
        result.push(option);
      }
    }

    return result;
  }

  private filterOptionGroup(propertyValues: Array<Option | OptionGroup>, typeRef: TypeReference, filter: (option: POption) => boolean) {
    return EngineInternalAPI.mapOptions(propertyValues, (v) => {
      const basicType = this.schema.getType(typeRef);
      if (basicType instanceof UnionType) {
        if (!basicType.types.includes(v.type.typeRef)) {
          return v;
        }
      } else {
        if (v.type.typeRef !== typeRef) {
          return v;
        }
      }
      return filter(v) ? v : null;
    }).filter(o => o !== null);
  }

  compatiblePropertyValuesByPath(model: PModel, propertyPath: PropertyPath): PropertyValues | undefined;
  compatiblePropertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: PropertyPath, transformer: IStructuralTransformer<T>): PropertyValues | undefined;
  compatiblePropertyValuesByPath(model: PModel, propertyPath: PropertyPath, transformer = new IdentityStructuralTransformer()): PropertyValues | undefined {
    const modelTypeRef = getTypeRef(model);
    if (modelTypeRef !== this.typeRef) {
      throw new Error(`Invalid type ref: ${modelTypeRef}. Expected type: ${this.typeRef}`);
    }

    let propertyValue: PropertyValue | undefined = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef).getPropertyValue();
    if (!propertyValue) {
      return undefined;
    }
    const values = this.getPropertyPotentiallyCompatibleValues(model, propertyPath);
    let propertyType = this.schema.getType(propertyValue.property.type);
    if (propertyType.kind === TypeKind.Enum) {
      return transformer.transform((values as any[]).filter(propValue => {
        const [, validationResult] = this.powerfulMutate(model, new SinglePropertyMutator(propertyPath, propValue))
        return validationResult.isValid;
      }));
    } else if (propertyType.kind === TypeKind.Primitive) {
      return values;
    }
    return this.compatibleOptionValues(values, propertyType, propertyValue, model, propertyPath, transformer);
  }

  compatibleOptionValuesByPath(model: PModel, propertyPath: PropertyPath): SourcedValueOptions | undefined;
  compatibleOptionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: PropertyPath, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;
  compatibleOptionValuesByPath(model: PModel, propertyPath: PropertyPath, transformer = new IdentityStructuralTransformer()): SourcedValueOptions | undefined {
    const typeRef = getTypeRef(model) ?? this.typeRef;
    let propertyValue: PropertyValue | undefined = new PropertyPathWalker(propertyPath, this.schema, model, typeRef).getPropertyValue();
    if (!propertyValue) {
      return undefined;
    }
    const values = this.getPropertyPotentiallyCompatibleValues(model, propertyPath);
    let propertyType = this.schema.getType(propertyValue.property.type);
    if (propertyType.kind === TypeKind.Enum) {
      return undefined;
    }
    return this.compatibleOptionValues(values, propertyType, propertyValue, model, propertyPath, transformer);
  }

  private compatibleOptionValues(values: SourcedValueOption[] | boolean[] | number[] | string[], propertyType: Type, propertyValue: PropertyValue, model: PModel<any>, propertyPath: string, transformer: IdentityStructuralTransformer) {
    const result = this.filterOptionGroup(values as any, propertyType.getTypeReference(), (propValue: any) => {
      let skipOption = false;
      const source = ModelSystemProperties.getSource(propertyValue);
      if (source && source.getOptionAvailability()) {
        for (let constraint of source.getOptionAvailability()!) {
          if (!constraint.evaluate(model, propValue)) {
            skipOption = true;
            break;
          }
        }
      }
      return !skipOption && this.powerfulMutate(model, new SinglePropertyMutator(propertyPath, propValue))[1].isValid;
    });
    return transformer.transform(result);
  }

  optionForPropertyByPath(model: Model, propertyPath: string): POption | undefined {
    return undefined;
  }

  compatibleOptionsByOption(option: POption, propertyPath: string): POption[] {
    return [];
  }

  public deleteConstraint(model: Model, constraint: ExpressionConstraint): any {
  }

  public initByProduct(schema: Schema, product: Product): PModel {
    return this.init(schema,
      product.type.typeRef,
      product.model,
      product.constraints,
      new Map<string, CompatibilityTable>(),
      product.filters)
  }

  //
  // initByProductGroup(schema: Schema, productGroup: ProductGroup): Model {
  //   return {
  //     model: productGroup.model,
  //     children: productGroup.children.map(c => {
  //       const typeRef = c.type.typeRef;
  //       const traverser = new PrototypeTraverser(schema, typeRef, c);
  //       const visitor = new SimpleVisitor(schema);
  //       const model = traverser.map(visitor)
  //       return model;
  //     })
  //   }
  // }

  public initByProductWithContext(schema: Schema, productWithContext: ProductWithContext): PModel {
    const product = productWithContext.product;
    return this.init(schema, product.type.typeRef, product.model, product.constraints, productWithContext.compatibilityTables);
  }

  public init(schema: Schema,
              typeRef: TypeReference,
              prototype: {},
              constraints: ExpressionConstraint[]                  = [],
              compatibilityTables: Map<string, CompatibilityTable> = new Map<string, CompatibilityTable>(),
              filters: FilterRegistry                              = null): PModel {
    this.schema = schema;
    this.filterRegistry = filters;
    this.typeRef = typeRef;
    this.constraints = constraints;
    this.compatibilityService = new CompatibilityService(this.schema);
    for (let [tableName, table] of compatibilityTables.entries()) {
      this.compatibilityService.addCompatibilityTable(table, tableName)
    }

    OclEngine.Utils.typeDeterminerFn = (obj: any) => {
      return obj[typeSymbol];
    };
    // check values against schema, check model validity and return copy of model
    let model = EngineInternalAPI.prepareModel(schema, typeRef, prototype);

    let [newModel, availChanges] = this.availabiltyService.applyModel(this.schema, model, this.typeRef);
    // this.setSourcedOptionToCompatible(newModel);
    let error = this.validate(newModel);
    if (error.isInvalid) {
      throw new Error(error.errorMessage);
    }
    return model;
  }

  // visible for testing
  mutate(model: PModel, mutator: ModelMutator): [PModel, ValidationResult] {
    return this.powerfulMutate(model, mutator, 0);
  }

  mutateWithOption(model: Model, option: POption, mutator: ModelOptionMutator): [Model, ValidationResult] {
    return [model, ValidationResult.Valid()];
  }

  private simpleMutate(model: PModel, ...mutators: ModelMutator[]): [PModel, ValidationResult, PropertyValue[]] {
    let availChanges: PropertyValue[] = [];
    let newModel = model;
    if (!EngineImpl.skipModelCopyOnMutate) {
      newModel = deepCopy(model);
    }
    for (let mutator of mutators) {
      let error;
      [newModel, error] = mutator.mutate(this, this.typeRef, newModel);
      if (error.isInvalid) {
        return [model, error, availChanges];
      }
    }
    [newModel, availChanges] = this.availabiltyService.applyModel(this.schema, newModel, this.typeRef);
    // this.setSourcedOptionToCompatible(newModel);
    let error = this.validate(newModel);
    return [newModel, error, availChanges];
  }


  private powerfulMutate(model: PModel, mutator: ModelMutator, cycleNum: number = 0): [PModel, ValidationResult] {
    if (cycleNum > 10) {
      return [model, ValidationResult.Error('Stabilization mutation error(recursion limit exceeded)!')]
    }
    let [newModel, error, availChanges] = this.simpleMutate(model, mutator);

    if (error.isInvalid && availChanges.length) {
      let variants: any[] = [];
      for (let achange of availChanges) {
        variants.push(this.propertyValuesByPath(newModel, achange.property.name));
      }
      let combinations = combi(...variants);
      for (let com of combinations) {
        let mutators: ModelMutator[] = [];
        let idx = 0;
        for (let achange of availChanges) {
          mutators.push(new SinglePropertyMutator(achange.property.name, com[idx++] as any))
        }
        let [pnew, perr] = this.simpleMutate(newModel, ...mutators);
        if (perr.isValid) {
          return [pnew, perr];
        }
      }
    }
    return [newModel, error];
  }


  propertiesState(model: Model): { available: Property[], unavailable: Property[] } {
    // this.schema.getType(model[typeSymbol]).
    // this.schema.getType()
    return { available: [], unavailable: [] };
  }

  isPropertyAvailable(propertyPath: string, model: Model): boolean {
    let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef).getPropertyValue();
    if (propertyValue) {
      return ModelSystemProperties.isAvailable(propertyValue);
    }
    return false;
  }


  validate(model: Model): ValidationResult {
    let checker = new ModelChecker();
    let validationResult = checker.checkModel(this.schema, model, this.typeRef);
    if (validationResult.isInvalid) {
      return validationResult;
    }

    this.schema.extendWithPrototype(model, (model as any)[typeSymbol]);

    const compatibilityTablesResult = this.validateCompatibilityTables(model);
    if (compatibilityTablesResult.isInvalid) {
      return compatibilityTablesResult;
    }
    for (const constraint of this.constraints) {
      if (!constraint.evaluate(model)) {
        return ValidationResult.Error(constraint.errorMessage);
      }
    }
    return ValidationResult.Valid();
  }

  setEnv(env: any) {
    OclParser.setEnv(env);
  }

  entitiesByComponents<T>(model: Model, components: TypeReference[]): IEntitySearchResult<T>[] {
    let type = getTypeRef(model as any);
    if (!type) {
      throw new Error(`Wrong model. Cannot find typeRef`);
    }
    const traverser = new PrototypeTraverser(this.schema, type, model);
    const visitor = new ComponentSearchVisitor<T>(this.schema, components);
    traverser.map(visitor);
    return visitor.searchResult;
  };

  private getPropertyPotentiallyCompatibleValues(model: PModel, propertyPath: PropertyPath) {
    let values: boolean[] | number[] | string[] | SourcedValueOptions;
    const compatibleValues = this.compatibilityService.getCompatibleWithModelValues(model, propertyPath);
    if (isRestrictedTo(compatibleValues)) {
      values = compatibleValues.values;
    } else {
      values = this.propertyValuesByPath(model, propertyPath);
    }
    return values;
  }

  private validateCompatibilityTables(model: Model): ValidationResult {
    return this.compatibilityService.validate(model);
  }

  getLookupValues(model: PModel, lookupId: LookupId): any[] | undefined {
    const compatibilityInvolvedPaths = this.compatibilityService.getCompatibilityInvolvedOptionPaths(model, this.typeRef);
    const resolutionSchema: ResolutionSchema = this.getResolutionSchema(this.typeRef);
    const resolutionInvolvedPaths = resolutionSchema.allOptions();
    const lookup = resolutionSchema.getLookupById(lookupId);
    if (!lookup) {
      throw new Error(`Cannot find lookup ${lookupId}`);
    }
    const { option: optionPath } = lookup;
    let properties: PropertyPath[];
    if (singlePropertyLookupTypeGuard(lookup)) {
      properties = [lookup.property];
    } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
      properties = [lookup.properties[lookup.key]];
    } else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
      properties = Object.values(lookup.properties);
    }

    let optionsPaths = this.mergeCompatibleWithResolutionOptions(compatibilityInvolvedPaths, resolutionInvolvedPaths, optionPath);

    const optionsPotentialValues = new Map<PropertyPath, Option[]>();

    for (let optionPath of optionsPaths) {
      optionsPotentialValues.set(optionPath, this.optionValuesByPath(model, optionPath, new FlatStructuralTransformer()));
    }
    const topFilters = resolutionSchema.topFiltersFor(lookupId, lookup.filterGroup);

    CSPUtils.applyFiltersTo(this.schema, topFilters, optionsPotentialValues, model);

    let currentOptionValues = optionsPotentialValues.get(optionPath);
    optionsPotentialValues.delete(optionPath);

    if (!currentOptionValues) {
      throw new Error(`No options available for ${lookupId}`);
    }

    let aggregateFunction: AggregateFunction<Option> = keepFirstAggregateFunction;
    let hasAggregateFunction = false;
    if (extendedSinglePropertyLookupTypeGuard(lookup) && lookup.aggregateExpression) {
      aggregateFunction = new Function('$prev, $next', lookup.aggregateExpression) as AggregateFunction<Option>
      hasAggregateFunction = true;
    }

    if (optionsPotentialValues.size == 0) {
      const set = EngineImpl.distinctPropertyValues(currentOptionValues, properties, aggregateFunction);
      return EngineImpl.toResult(set, lookup);
    }

    //Supports reference types: MLString, BigNumber, Object
    const validOptionPropertyValues = new ObjectHashSetWithPayload<Option>(aggregateFunction);

    // TODO: We can put only tables related to current option, example: Door w\o glass should not put doorGlass table
    const compatibilityFilter = new CompatibilityFilter(model, this.compatibilityService);
    const solver = new CSPSolver(compatibilityFilter, currentOptionValues, Array.from(optionsPotentialValues))
    solver.solve(optionPath, (currentOptionValue, _, q) => {
      const propertyValue = engineUtils.getPropertyPathValues(currentOptionValue.model, properties);
      validOptionPropertyValues.add(propertyValue, currentOptionValue);
      //Optimization: remove duplicates from the ongoing options
      // It can be done only in case there is no custom aggregateFunction provided for lookup
      if (!hasAggregateFunction) {
        const filteredOptionValues = [];
        const firstGroup = q[0];
        const len = firstGroup.length;
        let i = 0;
        for (; i < len; i++) {
          const value = firstGroup[i];
          if (!engineUtils.equals(engineUtils.getPropertyPathValues(value.model, properties), propertyValue)) {
            filteredOptionValues.push(value);
          }
        }
        q[0] = filteredOptionValues;
      }
      // Because current option value is the first option in cartesian and it matches
      // we can directly go to the next current option value
      return 'ResetFiltersAndContinueFromStart';
    });

    return EngineImpl.toResult(validOptionPropertyValues, lookup);
  }

  private static toResult(set: ObjectHashSetWithPayload<Option>, lookup: LookupRecord): any[] {
    const result: any[] = [];
    const values = set.toArray();
    if (singlePropertyLookupTypeGuard(lookup)) {
      for (const value of values) {
        result.push(value[0][0]);
      }
    } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
      for (let i = 0; i < values.length; i++) {
        const option = values[i][1];
        const value = engineUtils.getPropertyPathValues(option.model, Object.values(lookup.properties));
        result.push(engineUtils.buildObjectByKeys(value, Object.keys(lookup.properties)));
      }
    } else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
      const keys = Object.keys(lookup.properties);

      for (let i = 0; i < values.length; i++) {
        let value = values[i][0];
        const obj = engineUtils.buildObjectByKeys(value, keys);
        result.push(obj);
      }
    }
    return result;
  }

  private static distinctPropertyValues(values: Option[], properties: PropertyPath[], aggregateFunction: AggregateFunction<Option>) {
    const validOptionPropertyValues = new ObjectHashSetWithPayload<Option>(aggregateFunction);
    values.forEach(option => {
      const optionPropertyValue = engineUtils.getPropertyPathValues(option.model, properties);
      validOptionPropertyValues.add(optionPropertyValue, option);
    });
    return validOptionPropertyValues;
  }

  getResolutionSchema(typeRef: TypeReference): ResolutionSchema {
    return this.schema.resolutionSchemas.get(typeRef);
  }

  private mergeCompatibleWithResolutionOptions(compatibilityInvolvedPaths: PropertyPath[], resolutionInvolvedPaths: PropertyPath[], currentOptionPath: PropertyPath) {
    /*
      Include optionPath in optionsPaths:
       resolutionInvolvedPaths.topOptions  | resolutionInvolvedPaths.bottomOptions | compatibilityInvolvedPaths | Result
                +                          |                    +                  |          +                 |   +
                -                          |                    +                  |          -                 |   -

     */
    //TODO: Add support for indirect relationships: A->B, B->C => [A, B, C]

    // Property neither depend nor dependent
    if (!compatibilityInvolvedPaths.includes(currentOptionPath)) {
      return [currentOptionPath];
    }
    return compatibilityInvolvedPaths;
  }

  getLookupValue(model: PModel, lookupId: LookupId): any {
    const resolutionSchema = this.getResolutionSchema(this.typeRef);
    const lookup = resolutionSchema.getLookupById(lookupId);
    if (singlePropertyLookupTypeGuard(lookup)) {
      const path = TypeService.joinPath(lookup.option, lookup.property);
      return engineUtils.getPropertyPathValue(model, path);
    } else if (multiPropertiesLookupRecordTypeGuard(lookup) || extendedSinglePropertyLookupTypeGuard(lookup)) {
      const paths = Object.values(lookup.properties).map(p => TypeService.joinPath(lookup.option, p));
      const value = engineUtils.getPropertyPathValues(model, paths)
      return engineUtils.buildObjectByKeys(value, Object.keys(lookup.properties));
    }
  }

  isValueSelectedInLookup(model: PModel, value: any, lookupId: LookupId) {
    const resolutionSchema = this.getResolutionSchema(this.typeRef);
    const lookup = resolutionSchema.getLookupById(lookupId);
    if (!lookup) {
      throw new Error(`Cannot find lookup ${lookupId}`);
    }

    if (singlePropertyLookupTypeGuard(lookup)) {
      const path = TypeService.joinPath(lookup.option, lookup.property);
      return engineUtils.equals(engineUtils.getPropertyPathValue(model, path), value);
    } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
      const path = TypeService.joinPath(lookup.option, lookup.properties[lookup.key]);
      const selectedKeyValue = engineUtils.getPropertyPathValue(value, lookup.key);

      return engineUtils.equals(engineUtils.getPropertyPathValue(model, path), selectedKeyValue);
    } else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
      const paths = Object.values(lookup.properties).map(p => TypeService.joinPath(lookup.option, p));
      const modelValue = engineUtils.getPropertyPathValues(model, paths);
      const optionValue = engineUtils.getPropertyPathValues(value, Object.values(lookup.properties));
      return engineUtils.equals(optionValue, modelValue);
    }
    throw new Error('Unhandled lookup type');
  }
}

export class EngineContext {
  enterConstraint(constraintId: string) {
    // console.log('Enter Constraint', constraintId);
  }

  exitConstraint(constraintId: string) {
    // console.log('Exit Constraint', constraintId);
  }

  touchProperty(propertyId: string) {
    // console.log('Touched property: ', propertyId);
  }

  propertyChanged(propertyId: string) {
    // console.log('Changed property: ', propertyId);
  }
}

function combiInternal(list: any[][], size: number, ...args: any[]): any[][] {
  let idx = 0;
  let currentArg = args.shift();
  size = size / currentArg.length;
  while (idx < list.length) {
    for (let arg of currentArg) {
      for (let i = 0; i < size; i++) {
        list[idx++].push(arg);
      }
    }
  }
  if (args.length == 0) {
    return list;
  }
  return combiInternal(list, size, ...args);
}

export function combi(...args: any[]): any[][] {

  let size = 1;
  for (let arg of args) {
    size = size * arg.length;
  }
  let result = Array(size);
  for (let i = 0; i < size; i++) {
    result[i] = [];
  }
  return combiInternal(result, size, ...args);
}

function isSourcedValues(xs: (Option | OptionGroup)[] | number[] | string[]): xs is (Option | OptionGroup)[] {
  if (!xs.length) {
    return false;
  }
  const firstElement = xs[0];
  return (firstElement instanceof Option) || (firstElement instanceof OptionGroup);
}
